import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
    Box,
    Typography,
    Grid,
    useTheme,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Radio,
    Dialog,
} from '@mui/material';
import { Delete } from '../../assets/images';

import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import Button from '../../components/button';
import Dropdown from '../../components/dropdown';
import InputField from '../../components/formItem/inputField';
import CommonCard from '../../components/card/CommonCard';
import RadioBt, { RadioGroup } from '../../components/radioBt';
import { USER_GENDERS } from '../../constants';
import OrganizationSearchContent from '../../components/organizationSearchContent';
import { userAPI, membershipAPI, organizationAPI } from '../../actions/imsServer';
import {
    isEmail,
    isValidPassword,
} from '../../utils/password-validation';
import Popup from '../../components/popup';
import { formatDateTimeDisplay, getBirthDisplayValue, parseLocaleDateToYyyymmdd, isAdmin } from '../../utils/base';

const UserEdit = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const userData = location.state?.userData;

    // 어드민 여부 확인
    const meInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');

    // SNS 계정 여부 확인
    const isSnsAccount = !!(userData?.kakaoId || userData?.googleId);

    // SNS 계정 표시 텍스트 생성
    const getSnsAccountDisplayText = () => {
        if (!isSnsAccount) return formData.loginId;

        if (userData?.kakaoId) {
            return userData?.loginId ? `${t('kakao_account')} (${userData.loginId})` : t('kakao_account');
        } else if (userData?.googleId) {
            return userData?.loginId ? `${t('google_account')} (${userData.loginId})` : t('google_account');
        }

        return formData.loginId;
    };

    // 폼 데이터 상태
    const [formData, setFormData] = useState({
        // 기본 정보
        name: '',
        loginId: '',
        loginPwd: '',
        confirmLoginPwd: '',
        gender: '',
        birth: '',
        profileImage: null,
        profileImageUrl: '', // 기존 프로필 이미지 URL
        profilePath: '', // 프로필 이미지 경로 (profilePath 사용)

        // 소속 기관 정보
        organizations: []
    });

    // 소속 기관 추가/삭제 상태
    const [organizations, setOrganizations] = useState([]);

    // 기존 memberships 데이터 저장 (수정 모드에서 비교용)
    const [originalMemberships, setOriginalMemberships] = useState([]);

    // 기관 검색 팝업 상태
    const [isOrganizationSearchOpen, setIsOrganizationSearchOpen] = useState(false);

    // 로딩 상태
    const [isLoading, setIsLoading] = useState(false);

    // 팝업 상태
    const [popup, setPopup] = useState({
        open: false,
        message: '',
        subMessage: '',
        onConfirm: null
    });

    // 유효성 검사 상태
    const [validationErrors, setValidationErrors] = useState({});

    /** 생년월일: 포커스 시 숫자만 표시(로케일 포맷에서 백스페이스 무반응 방지) */
    const [birthFocused, setBirthFocused] = useState(false);

    // 중복확인 상태
    const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
    const [isAccountDuplicate, setIsAccountDuplicate] = useState(false);
    // 수정 모드일 때는 초기에 true로 설정 (계정이 변경되지 않았으면 항상 유효)
    const [accountChecked, setAccountChecked] = useState(userData ? true : false);

    // 중복확인 디바운스 타이머 참조
    const duplicateCheckTimerRef = React.useRef(null);

    // 실시간 유효성 검사
    const validateField = (field, value) => {
        const errors = { ...validationErrors };

        switch (field) {
            case 'name':
                if (!value || value.trim() === '') {
                    errors.name = t('please_enter_your_name');
                } else if (value.length < 2 || value.length > 16) {
                    errors.name = t('name_must_be_2_16_characters');
                } else {
                    delete errors.name;
                }
                break;

            case 'loginId':
                // SNS 계정인 경우 유효성 검사 제외
                if (isSnsAccount) {
                    delete errors.loginId;
                } else if (!value || value.trim() === '') {
                    errors.loginId = t('please_enter_your_account');
                } else if (!isEmail(value)) {
                    errors.loginId = t('please_enter_a_valid_email_address');
                } else {
                    delete errors.loginId;
                }
                break;

            case 'loginPwd':
                if (!userData) { // 추가 모드일 때만 검사
                    if (!value || value.trim() === '') {
                        errors.loginPwd = t('please_enter_your_password');
                    } else if (!isValidPassword(value)) {
                        errors.loginPwd = t('include_at_least_2_types_of_letters_numbers_and_symbols_6_12_characters');
                    } else {
                        delete errors.loginPwd;
                    }
                }
                break;

            case 'confirmLoginPwd':
                if (!userData) { // 추가 모드일 때만 검사
                    if (!value || value.trim() === '') {
                        errors.confirmLoginPwd = t('please_enter_password_confirmation');
                    } else if (formData.loginPwd !== value) {
                        errors.confirmLoginPwd = t('passwords_do_not_match');
                    } else {
                        delete errors.confirmLoginPwd;
                    }
                }
                break;

            case 'gender':
                if (value === '' || value === null || value === undefined) {
                    errors.gender = t('please_select_gender');
                } else {
                    delete errors.gender;
                }
                break;

            case 'birth':
                if (!value) {
                    errors.birth = t('please_enter_date_of_birth');
                } else {
                    const digits = String(value).replace(/[^\d]/g, '');
                    if (digits.length !== 8) {
                        errors.birth = t('please_enter_date_of_birth');
                    } else {
                        delete errors.birth;
                    }
                }
                break;

            default:
                break;
        }

        setValidationErrors(errors);
    };

    const handleBirthDateChange = (e) => {
        const value = e.target.value;
        const digitsOnly = value.replace(/[^\d]/g, '').slice(0, 8);
        const currentDigits = String(formData.birth ?? '').replace(/[^\d]/g, '');
        if (!value.trim()) {
            setFormData(prev => ({ ...prev, birth: '' }));
            validateField('birth', '');
            return;
        }
        if (digitsOnly.length < currentDigits.length && currentDigits.length > 0) {
            let next;
            if (digitsOnly === currentDigits.slice(0, digitsOnly.length)) {
                next = digitsOnly;
            } else {
                const removed = currentDigits.length - digitsOnly.length;
                next =
                    removed > 0
                        ? currentDigits.slice(0, currentDigits.length - removed)
                        : digitsOnly;
            }
            setFormData(prev => ({ ...prev, birth: next }));
            validateField('birth', next);
            return;
        }
        if (digitsOnly.length === 8) {
            const parsed = parseLocaleDateToYyyymmdd(digitsOnly, i18n.language);
            if (parsed) {
                setFormData(prev => ({ ...prev, birth: parsed }));
                validateField('birth', parsed);
                return;
            }
        }
        setFormData(prev => ({ ...prev, birth: digitsOnly }));
        validateField('birth', digitsOnly);
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        validateField(field, value);

        // 비밀번호가 변경되면 비밀번호 확인도 다시 검사
        if (field === 'loginPwd' && formData.confirmLoginPwd) {
            validateField('confirmLoginPwd', formData.confirmLoginPwd);
        }

        // 사용자계정이 변경되면 자동으로 중복확인 (디바운스 적용)
        // SNS 계정인 경우 중복확인 제외
        if (field === 'loginId' && !isSnsAccount) {
            // 기존 타이머가 있으면 취소
            if (duplicateCheckTimerRef.current) {
                clearTimeout(duplicateCheckTimerRef.current);
            }

            // 새로운 타이머 설정 (500ms 후 실행)
            duplicateCheckTimerRef.current = setTimeout(() => {
                checkAccountDuplicate(value);
            }, 500);
        }
    };

    // 팝업 표시 함수
    const showPopup = (message, subMessage = '', onConfirm = null) => {
        setPopup({
            open: true,
            message,
            subMessage,
            onConfirm: onConfirm || (() => setPopup(prev => ({ ...prev, open: false })))
        });
    };

    // 팝업 닫기 함수
    const closePopup = () => {
        setPopup(prev => ({ ...prev, open: false }));
    };

    // 소속기관 저장 함수 (추가 모드용)
    const saveMemberships = async (userId) => {
        try {
            for (const org of organizations) {
                const membershipData = {
                    userId: userId,
                    organizationId: org.id,
                    division: org.divisionSection1 || '',
                    section: org.divisionSection2 || '',
                    type: 1,
                    state: 0,
                    info: ''
                };

                await membershipAPI.registerMembership(membershipData);
            }
        } catch (error) {
            console.error('소속기관 저장 실패:', error);
            throw error; // 상위 함수에서 처리할 수 있도록 에러 전파
        }
    };

    // 소속기관 업데이트 함수 (수정 모드용)
    const updateMemberships = async (userId) => {
        try {
            // 기존 memberships를 organizationId로 매핑
            const originalMap = new Map();
            originalMemberships.forEach(m => {
                originalMap.set(m.organizationId, m);
            });

            // 현재 organizations를 organizationId로 매핑
            const currentMap = new Map();
            organizations.forEach(org => {
                currentMap.set(org.id, {
                    organizationId: org.id,
                    division: org.divisionSection1 || '',
                    section: org.divisionSection2 || '',
                    isMain: org.isMain
                });
            });

            // 삭제된 기관 처리 (기존에 있던데 현재 목록에 없음)
            for (const [orgId, originalMembership] of originalMap.entries()) {
                if (!currentMap.has(orgId)) {
                    // 삭제: state를 1로 업데이트
                    await membershipAPI.updateMembership({
                        userId: userId,
                        organizationId: orgId,
                        division: originalMembership.division || '',
                        section: originalMembership.section || '',
                        type: originalMembership.type || 1,
                        state: 1, // 삭제 상태
                        info: originalMembership.info || ''
                    });
                }
            }

            // 추가/수정된 기관 처리
            for (const [orgId, currentOrg] of currentMap.entries()) {
                const originalMembership = originalMap.get(orgId);

                if (!originalMembership) {
                    // 추가된 기관: registerMembership
                    await membershipAPI.registerMembership({
                        userId: userId,
                        organizationId: orgId,
                        division: currentOrg.division,
                        section: currentOrg.section,
                        type: 1,
                        state: 0,
                        info: ''
                    });
                } else {
                    // 기존 기관: 세부소속 변경 여부 확인
                    const divisionChanged = originalMembership.division !== currentOrg.division;
                    const sectionChanged = originalMembership.section !== currentOrg.section;

                    if (divisionChanged || sectionChanged) {
                        // 세부소속이 변경된 경우: updateMembership
                        await membershipAPI.updateMembership({
                            userId: userId,
                            organizationId: orgId,
                            division: currentOrg.division,
                            section: currentOrg.section,
                            type: originalMembership.type || 1,
                            state: 0,
                            info: originalMembership.info || ''
                        });
                    }
                    // 세부소속이 변경되지 않으면 아무것도 하지 않음
                }
            }
        } catch (error) {
            console.error('소속기관 업데이트 실패:', error);
            throw error;
        }
    };

    // 사용자계정 중복확인 (자동)
    const checkAccountDuplicate = async (account) => {
        if (!account || account.trim() === '') {
            setAccountChecked(false);
            setIsAccountDuplicate(false);
            return;
        }

        if (!isEmail(account)) {
            setAccountChecked(false);
            setIsAccountDuplicate(false);
            return;
        }

        setIsCheckingDuplicate(true);

        try {
            const response = await userAPI.checkUserExists({ loginId: account });

            if (response.data && response.data.exists) {
                setIsAccountDuplicate(true);
                setAccountChecked(true);
            } else {
                setIsAccountDuplicate(false);
                setAccountChecked(true);
            }
        } catch (error) {
            console.error('중복확인 실패:', error);
            setIsAccountDuplicate(false);
            setAccountChecked(false);
        } finally {
            setIsCheckingDuplicate(false);
        }
    };

    // 필수 필드 유효성 검사
    const isFormValid = () => {
        // 기본 필수 필드 검사
        const hasName = formData.name && formData.name.trim() !== '';
        // SNS 계정인 경우 loginId 검사 제외
        const hasLoginId = isSnsAccount ? true : (formData.loginId && formData.loginId.trim() !== '');
        // gender는 숫자 0도 유효하므로 명시적으로 체크
        const hasGender = formData.gender !== '' && formData.gender !== null && formData.gender !== undefined;
        const hasBirth = formData.birth && formData.birth.trim() !== '';

        // 추가 모드일 때만 비밀번호 검사
        const hasLoginPwd = userData ? true : (formData.loginPwd && formData.loginPwd.trim() !== '');
        const hasConfirmLoginPwd = userData ? true : (formData.confirmLoginPwd && formData.confirmLoginPwd.trim() !== '');

        // 소속기관 검사 (선택사항이지만 선택했을 때는 세부소속 필수)
        const hasValidOrganizations = organizations.length === 0 ||
            organizations.every(org => {
                // 소속기관이 divisionSections 정보가 없으면 세부소속 없이도 가능
                if (!org.divisionSections || Object.keys(org.divisionSections).length === 0) {
                    return true;
                }

                // divisionSections가 있으면 세부소속1은 필수
                const division1 = org.divisionSection1;
                if (!division1 || (typeof division1 === 'string' && division1.trim() === '')) {
                    return false;
                }

                // 세부소속2 옵션이 있으면 세부소속2도 필수
                const hasSection2Options = org.divisionSections[division1] &&
                    Array.isArray(org.divisionSections[division1]) &&
                    org.divisionSections[division1].length > 0;

                if (hasSection2Options) {
                    const division2 = org.divisionSection2;
                    if (!division2 || (typeof division2 === 'string' && division2.trim() === '')) {
                        return false;
                    }
                }

                return true;
            });

        // 소속기관이 있으면 주기관 선택 필수
        const hasMainOrganization = organizations.length === 0 || organizations.some(org => org.isMain);

        // 유효성 검사 에러가 없는지 확인
        const hasNoValidationErrors = Object.keys(validationErrors).length === 0;

        // 중복확인 검사
        // SNS 계정인 경우 중복확인 검사 제외
        let isAccountValid = false;
        if (isSnsAccount) {
            // SNS 계정인 경우 항상 유효
            isAccountValid = true;
        } else if (userData) {
            // 수정 모드
            const originalLoginId = userData.loginId || '';
            const currentLoginId = formData.loginId || '';
            const accountChanged = originalLoginId !== currentLoginId;

            if (!accountChanged) {
                // 계정이 변경되지 않았으면 항상 유효
                isAccountValid = true;
            } else {
                // 계정이 변경되었으면 중복확인 완료되고 중복이 아니어야 함
                // 중복확인 중이 아닐 때만 체크
                isAccountValid = !isCheckingDuplicate && accountChecked && !isAccountDuplicate;
            }
        } else {
            // 추가 모드: 중복확인 완료되고 중복이 아니어야 함
            // 중복확인 중이 아닐 때만 체크
            isAccountValid = !isCheckingDuplicate && accountChecked && !isAccountDuplicate;
        }

        const isValid = hasName && hasLoginId && hasLoginPwd && hasConfirmLoginPwd &&
            hasGender && hasBirth && hasValidOrganizations && hasMainOrganization &&
            hasNoValidationErrors && isAccountValid;

        return isValid;
    };

    // 폼 유효성 검사
    const validateForm = () => {
        const errors = [];

        // 필수 필드 검사
        if (!formData.name || formData.name.trim() === '') {
            errors.push(t('please_enter_your_name'));
        } else if (formData.name.length < 2 || formData.name.length > 16) {
            errors.push(t('name_must_be_2_16_characters'));
        }

        // SNS 계정인 경우 loginId 검사 제외
        if (!isSnsAccount) {
            if (!formData.loginId || formData.loginId.trim() === '') {
                errors.push(t('please_enter_your_account'));
            } else if (!isEmail(formData.loginId)) {
                errors.push(t('please_enter_a_valid_email_address'));
            }
        }

        if (!userData) {
            // 추가 모드일 때만 비밀번호 검사
            if (!formData.loginPwd || formData.loginPwd.trim() === '') {
                errors.push(t('please_enter_your_password'));
            } else if (!isValidPassword(formData.loginPwd)) {
                errors.push(t('include_at_least_2_types_of_letters_numbers_and_symbols_6_12_characters'));
            }

            if (formData.loginPwd !== formData.confirmLoginPwd) {
                errors.push(t('passwords_do_not_match'));
            }
        }

        // gender는 숫자 0도 유효하므로 명시적으로 체크
        if (formData.gender === '' || formData.gender === null || formData.gender === undefined) {
            errors.push(t('please_select_gender'));
        }

        if (!formData.birth || formData.birth.trim() === '') {
            errors.push(t('please_enter_date_of_birth'));
        }

        // 소속기관 검사 (선택사항이지만 선택했을 때는 세부소속 필수)
        if (organizations.length > 0) {
            organizations.forEach((org, index) => {
                // divisionSections 정보가 없으면 세부소속 없이도 가능
                if (org.divisionSections && Object.keys(org.divisionSections).length > 0) {
                    // divisionSections가 있으면 세부소속1은 필수
                    const division1 = org.divisionSection1;
                    if (!division1 || (typeof division1 === 'string' && division1.trim() === '')) {
                        errors.push(t('please_select_division_1_for_organization_n', { n: index + 1 }));
                    } else {
                        // 세부소속2 옵션이 있으면 세부소속2도 필수
                        const hasSection2Options = org.divisionSections[division1] &&
                            Array.isArray(org.divisionSections[division1]) &&
                            org.divisionSections[division1].length > 0;

                        if (hasSection2Options) {
                            const division2 = org.divisionSection2;
                            if (!division2 || (typeof division2 === 'string' && division2.trim() === '')) {
                                errors.push(t('please_select_division_2_for_organization_n', { n: index + 1 }));
                            }
                        }
                    }
                }
            });

            // 소속기관이 있으면 주기관 선택 필수
            const hasMainOrganization = organizations.some(org => org.isMain);
            if (!hasMainOrganization) {
                errors.push(t('please_select_a_main_organization'));
            }
        }

        return errors;
    };

    const handleSubmit = async () => {
        // 폼 유효성 검사
        const errors = validateForm();
        if (errors.length > 0) {
            showPopup(errors.join('\n'));
            return;
        }

        setIsLoading(true);

        try {
            // 선택된 주기관 ID
            const mainOrganizationId = organizations.find(org => org.isMain)?.id;

            // 기존 주기관 ID (수정 모드에서 비교용)
            const originalMainOrgId = userData?.mainOrganizationId || userData?.mainorganizationid;

            // 주기관 변경 여부 확인 (수정 모드에서만)
            const mainOrgChanged = userData && mainOrganizationId !== originalMainOrgId;

            const birthParsed = parseLocaleDateToYyyymmdd(formData.birth, i18n.language);
            const birthYyyymmdd = birthParsed || String(formData.birth ?? '').replace(/[^\d]/g, '').slice(0, 8);

            const submitData = {
                // SNS 계정인 경우 loginId와 email을 수정하지 않음
                ...(isSnsAccount ? {} : {
                    loginId: formData.loginId.trim(),
                    email: formData.loginId.trim()
                }),
                loginPwd: formData.loginPwd,
                grade: 1,
                state: 0,
                name: formData.name.trim(),
                birth: birthYyyymmdd.length === 8 ? birthYyyymmdd : formData.birth,
                gender: formData.gender,
            };

            // 주기관이 선택된 경우 또는 주기관이 변경된 경우 mainOrganizationId 포함
            if (mainOrganizationId) {
                // 추가 모드이거나 수정 모드에서 주기관이 변경된 경우
                if (!userData || mainOrgChanged) {
                    submitData.mainOrganizationId = mainOrganizationId;
                }
            }

            // 추가 모드일 때만 비밀번호 포함
            if (!userData) {
                submitData.loginPwd = formData.loginPwd;
            }

            if (userData) {
                // 수정 모드
                // updateUser는 id가 필요함
                submitData.id = userData.id;
                await userAPI.updateUser(submitData);

                // updateUser는 response.data를 반환하므로 에러가 없으면 성공
                // 프로필 이미지 업로드 (파일이 있는 경우)
                if (formData.profileImage && userData.id) {
                    try {
                        const uploadData = {
                            userId: userData.id,
                            file: formData.profileImage
                        };
                        await userAPI.uploadProfileImage(uploadData);
                    } catch (uploadError) {
                        console.error('프로필 사진 업로드 실패:', uploadError);
                        // 프로필 사진 업로드 실패해도 사용자 정보 수정은 성공으로 처리
                    }
                }

                // 소속기관 업데이트 (기존과 비교하여 변경사항만 업데이트)
                try {
                    await updateMemberships(userData.id);
                    showPopup(t('user_info_updated'), '', () => {
                        closePopup();
                        navigate('/users');
                    });
                } catch (membershipError) {
                    console.error('소속기관 업데이트 실패:', membershipError);
                    showPopup('사용자 정보는 수정되었지만 소속기관 저장에 실패했습니다.', '다시 시도해주세요.');
                }
            } else {
                // 추가 모드
                const response = await userAPI.registerUser(submitData);

                if (response.result === 'created') {
                    const userId = response.data?.user?.id;

                    // 사용자 등록 성공 후 프로필 사진이 있으면 업로드
                    if (formData.profileImage && userId) {
                        try {
                            const uploadData = {
                                userId: userId,
                                file: formData.profileImage
                            };

                            await userAPI.uploadProfileImage(uploadData);
                        } catch (uploadError) {
                            console.error('프로필 사진 업로드 실패:', uploadError);
                            // 프로필 사진 업로드 실패해도 사용자 등록은 성공으로 처리
                        }
                    }

                    // 소속기관 저장
                    if (userId) {
                        try {
                            await saveMemberships(userId);
                            showPopup(t('user_added'), '', () => {
                                closePopup();
                                navigate('/users');
                            });
                        } catch (membershipError) {
                            showPopup(t('user_added_membership_save_failed'), t('try_again'));
                        }
                    } else {
                        showPopup(t('user_added'), '', () => {
                            closePopup();
                            navigate('/users/list');
                        });
                    }
                }
            }
        } catch (error) {
            console.error('사용자 저장 실패:', error);

            if (error.response?.data?.message === 'main_organization_change_limit_per_month') {
                showPopup(t('main_organization_change_limit_exceeded'));
            } else {
                showPopup(t('user_save_failed'), t('try_again'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/users');
    };

    // 소속 기관 추가
    const handleAddOrganization = () => {
        if (organizations.length < 5) {
            setIsOrganizationSearchOpen(true);
        }
    };

    // 소속 기관 삭제
    const handleRemoveOrganization = (id) => {
        setOrganizations(prev => {
            const filtered = prev.filter(org => org.id !== id);
            // 주기관이 삭제된 경우 첫 번째 기관을 주기관으로 설정
            if (filtered.length > 0 && !filtered.some(org => org.isMain)) {
                filtered[0].isMain = true;
            }
            return filtered;
        });
    };

    // 주기관 변경
    const handleMainOrganizationChange = (id) => {
        setOrganizations(prev => prev.map(org => ({
            ...org,
            isMain: org.id === id
        })));
    };

    // 소속 기관 정보 변경
    const handleOrganizationChange = (id, field, value) => {
        setOrganizations(prev => prev.map(org => {
            if (org.id === id) {
                // Dropdown에서 반환되는 값이 객체인 경우 value만 추출
                const actualValue = typeof value === 'object' && value !== null ? value.value : value;

                const updatedOrg = { ...org, [field]: actualValue };
                // 세부소속1이 변경되면 세부소속2 초기화
                if (field === 'divisionSection1') {
                    updatedOrg.divisionSection2 = '';
                }
                return updatedOrg;
            }
            return org;
        }));
    };

    // 기관 검색에서 선택된 기관 추가
    const handleSelectOrganization = (organization) => {
        // 이미 추가된 기관인지 확인
        const isAlreadyAdded = organizations.some(org => org.id === organization.id);

        if (isAlreadyAdded) {
            showPopup(t('organization_already_added'), t('select_different_organization'));
            return;
        }

        const newOrg = {
            id: organization.id,
            organizationName: organization.name || organization.organizationName,
            divisionSections: organization.divisionSections,
            divisionSection1: '',
            divisionSection2: '',
            isMain: organizations.length === 0 // 첫 번째 기관은 자동으로 주기관
        };

        setOrganizations(prev => [...prev, newOrg]);

        setIsOrganizationSearchOpen(false);
    };

    // 파일 업로드 핸들러
    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            // 파일 크기 제한 (5MB)
            if (file.size > 5 * 1024 * 1024) {
                showPopup(t('file_size_max_5mb'));
                return;
            }
            // 파일 형식 제한 (이미지 파일)
            const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
            if (!allowedTypes.includes(file.type)) {
                showPopup(t('only_jpeg_png_upload_allowed'));
                return;
            }

            // 파일을 임시로 저장 (저장하기 버튼 클릭 시 업로드)
            setFormData(prev => ({ ...prev, profileImage: file }));
        }
    };

    // 기존 사용자 데이터가 있으면 폼에 채우기
    useEffect(() => {
        const toBirthYyyymmdd = (value) => {
            if (!value) return '';
            const str = String(value).replace(/[^0-9]/g, '');
            return str.length === 8 ? str : value;
        };

        const loadUserForEdit = async () => {
            if (!userData) return;
            try {
                setIsLoading(true);
                // const response = await userAPI.getUsers(userData.id);
                // const dataRoot = response?.data || response;
                // const user = dataRoot?.user || dataRoot;

                // 기본 정보 세팅 (생년월일: API yyyymmdd 8자리 그대로 저장)
                setFormData(prev => ({
                    ...prev,
                    name: userData.name || '',
                    loginId: userData.loginId || '',
                    gender: userData.gender,
                    birth: toBirthYyyymmdd(userData.birth),
                    profileImageUrl: userData.profileImageUrl || userData.profilePath || '',
                    profilePath: userData.profilePath || userData.profileImageUrl || '',
                }));


                // 주기관 ID는 userData에서 판별
                const mainOrgId = userData.mainOrganizationId || userData.mainorganizationid;

                let formattedOrganizations = [];

                // 멤버십으로부터 불러오기
                try {
                    const membershipsRes = await membershipAPI.getMemberships({ userId: userData.id, state: 0 });
                    const memberships = (
                        membershipsRes?.data?.memberships ||
                        membershipsRes?.memberships ||
                        (Array.isArray(membershipsRes) ? membershipsRes : [])
                    );

                    // 기존 memberships 저장 (비교용) - state: 0인 활성 상태만 저장
                    const activeMemberships = (memberships || []).filter(m => m.state === 0 || m.state === undefined || m.state === null);
                    setOriginalMemberships(activeMemberships);

                    if (Array.isArray(memberships) && memberships.length > 0) {
                        // 각 멤버십의 기관 상세를 조회해서 이름/세부분류 확보
                        const orgIds = Array.from(new Set(memberships.map(m => m.organizationId))).filter(Boolean);
                        const orgDetailMap = {};
                        for (const orgId of orgIds) {
                            try {
                                const orgRes = await organizationAPI.getOrganizations({ id: orgId });
                                const listRaw = orgRes?.data?.organizations || [];
                                const list = Array.isArray(listRaw) ? listRaw : [listRaw];
                                const found = list.find(item => (item?.id === orgId || item?.organizationId === orgId));
                                if (found) orgDetailMap[orgId] = found;
                            } catch (e) {
                                // 개별 실패 무시
                            }
                        }

                        formattedOrganizations = memberships.map(m => {
                            const detail = orgDetailMap[m.organizationId] || {};
                            // divisionSections 정규화 (문자열 JSON -> 객체)
                            let divisionSections = detail.divisionSections || {};
                            if (typeof divisionSections === 'string') {
                                try { divisionSections = JSON.parse(divisionSections); } catch (_) { divisionSections = {}; }
                            }
                            return {
                                id: m.organizationId,
                                organizationName: detail.organizationName || detail.name || '',
                                divisionSections,
                                divisionSection1: m.division || '',
                                divisionSection2: m.section || '',
                                isMain: m.organizationId === mainOrgId,
                            };
                        });
                    }
                } catch (e) {
                    // 멤버십 불러오기 실패는 무시하고 계속 진행
                }


                // divisionSections가 비어있는 기관들에 대해 보강 (필요 시)
                const needDivisionSections = formattedOrganizations.filter(o => !o.divisionSections || Object.keys(o.divisionSections).length === 0);
                if (needDivisionSections.length > 0) {
                    // 중복 호출 방지 위해 고유 ID만 조회
                    const uniqueIds = Array.from(new Set(needDivisionSections.map(o => o.id))).filter(Boolean);
                    const divisionsByOrgId = {};
                    // 순차 호출 (안전성 우선). API 스펙에 따라 params로 id 필터링 지원 가정
                    for (const orgId of uniqueIds) {
                        try {
                            const orgRes = await organizationAPI.getOrganizations({ id: orgId });
                            const listRaw = orgRes?.data?.organizations || [];
                            const list = Array.isArray(listRaw) ? listRaw : [listRaw];
                            const found = list.find(item => (item?.id === orgId || item?.organizationId === orgId));
                            if (found && found.divisionSections) {
                                let ds = found.divisionSections;
                                if (typeof ds === 'string') {
                                    try { ds = JSON.parse(ds); } catch (_) { ds = {}; }
                                }
                                divisionsByOrgId[orgId] = ds;
                            }
                        } catch (e) {
                            // 개별 실패는 무시하고 계속 진행
                        }
                    }

                    formattedOrganizations = formattedOrganizations.map(o => ({
                        ...o,
                        divisionSections: (o.divisionSections && Object.keys(o.divisionSections).length > 0)
                            ? o.divisionSections
                            : (divisionsByOrgId[o.id] || {}),
                    }));
                }

                setOrganizations(formattedOrganizations);

                // 유효성 검사/중복확인 상태 초기화
                setValidationErrors({});
                setAccountChecked(true);
                setIsAccountDuplicate(false);
            } catch (err) {
                console.error('사용자 상세 불러오기 실패:', err);
                // 최소한 기존 userData로 세팅
                setFormData(prev => ({
                    ...prev,
                    name: userData.name || '',
                    loginId: userData.loginId || '',
                    gender: userData.gender,
                    birth: toBirthYyyymmdd(userData.birth),
                    profileImageUrl: userData.profileImageUrl || userData.profilePath || '',
                    profilePath: userData.profilePath || userData.profileImageUrl || '',
                }));
                if (userData.organizations && userData.organizations.length > 0) {
                    const formattedOrganizations = userData.organizations.map(org => ({
                        id: org.organizationId || org.id,
                        organizationName: org.organizationName || org.name,
                        divisionSections: org.divisionSections || {},
                        divisionSection1: org.divisionSection1 || '',
                        divisionSection2: org.divisionSection2 || '',
                        isMain: (org.organizationId || org.id) === (userData.mainOrganizationId || userData.mainorganizationid)
                    }));
                    setOrganizations(formattedOrganizations);
                }
                setValidationErrors({});
                setAccountChecked(true);
                setIsAccountDuplicate(false);
            } finally {
                setIsLoading(false);
            }
        };

        loadUserForEdit();
    }, [userData]);

    // 추가 모드일 때 어드민이면 내 기관 자동 추가
    useEffect(() => {
        const loadAdminOrganization = async () => {
            // 수정 모드이거나 어드민이 아니면 리턴
            if (userData || !isAdmin(meInfo?.grade) || !meInfo?.id) return;

            try {
                // 어드민의 기관 조회 (grade 50, 60 모두 membership에서 가져오기)
                const membershipsResponse = await membershipAPI.getMemberships({ userId: meInfo.id, type: 0, state: 0, expand: 'organization' });
                const memberships = membershipsResponse?.data?.memberships || [];
                const myOrg = memberships.length > 0 ? memberships[0].organization : null;

                if (myOrg) {

                    // divisionSections 정규화
                    let divisionSections = myOrg.divisionSections || {};
                    if (typeof divisionSections === 'string') {
                        try {
                            divisionSections = JSON.parse(divisionSections);
                        } catch (_) {
                            divisionSections = {};
                        }
                    }

                    // 내 기관을 organizations에 추가 (주기관으로 설정)
                    const adminOrg = {
                        id: myOrg.id,
                        organizationName: myOrg.organizationName || myOrg.name || '',
                        divisionSections,
                        divisionSection1: '',
                        divisionSection2: '',
                        isMain: true, // 주기관으로 설정
                    };

                    setOrganizations([adminOrg]);
                }
            } catch (err) {
                console.error('어드민 기관 불러오기 실패:', err);
            }
        };

        loadAdminOrganization();
    }, [userData, meInfo?.grade, meInfo?.id]);

    // 컴포넌트 언마운트 시 타이머 정리
    useEffect(() => {
        return () => {
            if (duplicateCheckTimerRef.current) {
                clearTimeout(duplicateCheckTimerRef.current);
            }
        };
    }, []);

    // 항상 비밀번호가 일치할 때 confirmLoginPwd 에러를 삭제해주어, helperText가 안 남도록 보완
    // 핵심 부분(만약 위 케이스에서 타이밍 이슈로 삭제가 안 된 경우 대비)
    useEffect(() => {
        if ((formData.loginPwd && formData.confirmLoginPwd) && formData.loginPwd === formData.confirmLoginPwd) {
            if (validationErrors.confirmLoginPwd) {
                setValidationErrors(prev => {
                    const { confirmLoginPwd, ...rest } = prev;
                    return rest;
                });
            }
        }
    }, [formData.loginPwd, formData.confirmLoginPwd, setValidationErrors]);

    // 계정 탈퇴 핸들러
    const handleUnregister = async () => {
        if (!userData?.id) {
            setPopup({
                open: true,
                message: t('user_info_not_found'),
                subMessage: '',
                onConfirm: () => setPopup(prev => ({ ...prev, open: false }))
            });
            return;
        }

        // 탈퇴 확인 팝업
        setPopup({
            open: true,
            message: t('confirm_account_withdrawal'),
            onConfirm: async () => {
                setPopup(prev => ({ ...prev, open: false }));

                try {
                    setIsLoading(true);

                    // 계정 탈퇴
                    await userAPI.unregisterUser({
                        type: 1, // delete
                        userId: userData.id
                    });

                    setPopup({
                        open: true,
                        message: t('account_withdrawn'),
                        subMessage: '',
                        onConfirm: () => {
                            setPopup(prev => ({ ...prev, open: false }));
                            navigate('/users');
                        }
                    });
                } catch (error) {
                    console.error('계정 탈퇴 실패:', error);
                    setPopup({
                        open: true,
                        message: t('account_withdrawal_failed'),
                        subMessage: error.response?.data?.message || t('try_again'),
                        onConfirm: () => setPopup(prev => ({ ...prev, open: false }))
                    });
                } finally {
                    setIsLoading(false);
                }
            },
            onCancel: () => setPopup(prev => ({ ...prev, open: false }))

        });
    };


    return (
        <ContentArea padding='20px 10px 10px 10px' sx={{ height: '100vh', overflowY: 'auto' }}>
            {/* Navigation with 탈퇴 버튼 */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, px: 2.5, pb: 3, alignSelf: 'stretch' }}>
                <Button
                    variant="gray"
                    onClick={handleUnregister}
                    disabled={isLoading}
                >
                    {t('withdraw_account')}
                </Button>
            </Box>

            {/* 폼 영역 */}
            <Box sx={{
                px: { xs: 2, sm: 3 },
                pb: 4,
                width: '100%',
                maxWidth: '100%',
                display: 'flex',
            }}>
                <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ flex: 1 }}>
                    {/* 왼쪽 컬럼 - 기본 정보 */}
                    <Grid item xs={4} lg={4} xl={4}>
                        <CommonCard sx={{ alignItems: 'stretch', gap: 3 }}>
                            <Box>
                                <Typography sx={{
                                    mb: 1,
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    letterSpacing: '-0.42px',
                                    textAlign: 'left'
                                }}>
                                    {t('name')} *
                                </Typography>
                                <InputField
                                    placeholder={t('name_placeholder_2_16')}
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    error={!!validationErrors.name}
                                    helperText={validationErrors.name || ' '}
                                />
                            </Box>

                            <Box>
                                <Typography sx={{
                                    mb: 1,
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    letterSpacing: '-0.42px',
                                    textAlign: 'left'
                                }}>
                                    {t('user_account')} *
                                </Typography>
                                <InputField
                                    placeholder={t('email_format_placeholder')}
                                    value={isSnsAccount ? getSnsAccountDisplayText() : formData.loginId}
                                    onChange={(e) => handleInputChange('loginId', e.target.value)}
                                    disabled={isSnsAccount}
                                    error={isSnsAccount ? false : (!!validationErrors.loginId || (accountChecked && isAccountDuplicate))}
                                    helperText={
                                        isSnsAccount ? ' ' : (
                                            validationErrors.loginId ||
                                            (accountChecked && isAccountDuplicate ? t('account_in_use') : '') ||
                                            (accountChecked && !isAccountDuplicate && !isCheckingDuplicate && formData.loginId ? t('account_is_available') : '') ||
                                            (isCheckingDuplicate ? t('checking_duplicate') : '') ||
                                            ' '
                                        )
                                    }
                                />
                            </Box>

                            {!userData && (
                                <>
                                    <Box>
                                        <Typography sx={{
                                            mb: 1,
                                            fontWeight: 500,
                                            fontSize: '14px',
                                            letterSpacing: '-0.42px',
                                            textAlign: 'left'
                                        }}>
                                            {t('account_password')} *
                                        </Typography>
                                        <InputField
                                            type="password"
                                            showVisibilityIcon={true}
                                            placeholder="영문자, 숫자, 기호 중 2개이상 포함 6~12자"
                                            value={formData.loginPwd}
                                            onChange={(e) => handleInputChange('loginPwd', e.target.value)}
                                            error={(formData.loginPwd && formData.confirmLoginPwd && formData.loginPwd !== formData.confirmLoginPwd) || !!validationErrors.loginPwd}
                                            helperText={(formData.loginPwd && formData.confirmLoginPwd && formData.loginPwd !== formData.confirmLoginPwd) ? '비밀번호가 일치하지 않습니다.' : (validationErrors.loginPwd || ' ')}
                                        />
                                    </Box>

                                    <Box>
                                        <Typography sx={{
                                            mb: 1,
                                            fontWeight: 500,
                                            fontSize: '14px',
                                            letterSpacing: '-0.42px',
                                            textAlign: 'left'
                                        }}>
                                            {t('account_password_confirmation')} *
                                        </Typography>
                                        <InputField
                                            type="password"
                                            showVisibilityIcon={true}
                                            placeholder={t('password_requirement_placeholder')}
                                            value={formData.confirmLoginPwd}
                                            onChange={(e) => handleInputChange('confirmLoginPwd', e.target.value)}
                                            error={(formData.loginPwd && formData.confirmLoginPwd && formData.loginPwd !== formData.confirmLoginPwd) || !!validationErrors.confirmLoginPwd}
                                            helperText={(formData.loginPwd && formData.confirmLoginPwd && formData.loginPwd !== formData.confirmLoginPwd) ? t('passwords_do_not_match') : (validationErrors.confirmLoginPwd || ' ')}
                                        />
                                    </Box>
                                </>
                            )}

                            <Box>
                                <Typography sx={{
                                    mb: 1,
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    letterSpacing: '-0.42px',
                                    textAlign: 'left'
                                }}>
                                    {t('gender')} *
                                </Typography>
                                <RadioGroup
                                    value={formData.gender}
                                    onChange={(e) => handleInputChange('gender', e.target.value)}
                                >
                                    {USER_GENDERS.map((gender) => (
                                        <RadioBt
                                            key={gender.value}
                                            value={gender.value}
                                            label={t(gender.label)}
                                        />
                                    ))}
                                </RadioGroup>
                                {validationErrors.gender && (
                                    <Typography sx={{
                                        color: 'error.main',
                                        fontSize: '12px',
                                        mt: 0.5
                                    }}>
                                        {validationErrors.gender}
                                    </Typography>
                                )}
                                {!validationErrors.gender && (
                                    <Typography sx={{
                                        fontSize: '12px',
                                        mt: 0.5,
                                        visibility: 'hidden'
                                    }}>
                                        &nbsp;
                                    </Typography>
                                )}
                            </Box>

                            <Box>
                                <Typography sx={{
                                    mb: 1,
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    letterSpacing: '-0.42px',
                                    textAlign: 'left'
                                }}>
                                    {t('birth_date')} *
                                </Typography>
                                <Box sx={{ position: 'relative' }}>
                                    <InputField
                                        value={
                                            birthFocused
                                                ? String(formData.birth ?? '')
                                                    .replace(/[^\d]/g, '')
                                                    .slice(0, 8)
                                                : getBirthDisplayValue(formData.birth)
                                        }
                                        onChange={handleBirthDateChange}
                                        onFocus={() => setBirthFocused(true)}
                                        onBlur={() => setBirthFocused(false)}
                                        placeholder={formatDateTimeDisplay('1990-03-15', 'date')}
                                        inputProps={{
                                            inputMode: 'numeric',
                                            pattern: '[0-9]*',
                                            maxLength: birthFocused ? 8 : 14,
                                        }}
                                        error={!!validationErrors.birth}
                                        helperText={validationErrors.birth || ' '}
                                    />
                                </Box>
                            </Box>

                            <Box>
                                <Typography sx={{
                                    mb: 1,
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    letterSpacing: '-0.42px',
                                    textAlign: 'left'
                                }}>
                                    {t('profile_photo')}
                                </Typography>
                                <Box sx={{
                                    border: `2px dashed ${theme.palette.figma.white[20]}`,
                                    borderRadius: '8px',
                                    p: 4,
                                    textAlign: 'center',
                                    backgroundColor: theme.palette.figma.white[5],
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: theme.palette.figma.white[10],
                                        borderColor: theme.palette.figma.white[30]
                                    }
                                }}>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileUpload}
                                        style={{ display: 'none' }}
                                        id="profile-upload"
                                    />
                                    <label htmlFor="profile-upload" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                                        {(formData.profileImage || formData.profileImageUrl || formData.profilePath) ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <img
                                                    src={formData.profileImage ?
                                                        URL.createObjectURL(formData.profileImage) :
                                                        (formData.profilePath || formData.profileImageUrl)
                                                    }
                                                    alt="Profile"
                                                    style={{
                                                        width: '80px',
                                                        height: '80px',
                                                        borderRadius: '50%',
                                                        objectFit: 'cover',
                                                        marginBottom: '8px'
                                                    }}
                                                    onError={(e) => {
                                                        // 이미지 로드 실패 시 기본 아이콘 표시
                                                        e.target.style.display = 'none';
                                                        const parent = e.target.parentElement;
                                                        if (parent) {
                                                            const placeholder = parent.querySelector('.profile-placeholder');
                                                            if (placeholder) placeholder.style.display = 'flex';
                                                        }
                                                    }}
                                                />
                                                <Typography sx={{
                                                    color: theme.palette.figma.white[70],
                                                    fontSize: '12px'
                                                }}>
                                                    {t('click_to_change_photo')}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <Box sx={{
                                                    width: '80px',
                                                    height: '80px',
                                                    borderRadius: '50%',
                                                    backgroundColor: theme.palette.figma.white[10],
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    marginBottom: '8px',
                                                    border: `2px solid ${theme.palette.figma.white[20]}`
                                                }}>
                                                    <Typography sx={{
                                                        color: theme.palette.figma.white[40],
                                                        fontSize: '24px'
                                                    }}>
                                                        👤
                                                    </Typography>
                                                </Box>
                                                <Typography sx={{
                                                    color: theme.palette.figma.white[70],
                                                    fontSize: '12px'
                                                }}>
                                                    {t('click_to_upload_photo')}
                                                </Typography>
                                            </Box>
                                        )}
                                    </label>
                                </Box>
                            </Box>
                        </CommonCard>
                    </Grid>

                    {/* 오른쪽 컬럼 - 소속 기관 */}
                    <Grid item xs={8} lg={8} xl={8} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CommonCard sx={{ alignItems: 'stretch', gap: 3 }}>
                            <Box>
                                <Typography sx={{
                                    mb: 2,
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    letterSpacing: '-0.42px',
                                    textAlign: 'left'
                                }}>
                                    {t('affiliated_organization')}
                                </Typography>

                                {/* 소속 기관 테이블 */}
                                <TableContainer component={Paper} sx={{
                                    backgroundColor: theme.palette.figma.white[5],
                                    border: `1px solid ${theme.palette.figma.white[20]}`,
                                    borderRadius: '8px',
                                    overflowX: 'auto',
                                    maxWidth: '100%'
                                }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{
                                                backgroundColor: theme.palette.figma.white[10],
                                                borderBottom: `1px solid ${theme.palette.figma.white[20]}`
                                            }}>
                                                <TableCell sx={{
                                                    fontWeight: 600,
                                                    fontSize: '14px',
                                                    color: theme.palette.text?.primary || 'rgba(255, 255, 255, 0.9)',
                                                    border: 'none'
                                                }}>
                                                    {t('affiliated_organization')}
                                                </TableCell>
                                                <TableCell sx={{
                                                    fontWeight: 600,
                                                    fontSize: '14px',
                                                    color: theme.palette.text?.primary || 'rgba(255, 255, 255, 0.9)',
                                                    border: 'none'
                                                }}>
                                                    {t('division_1')}
                                                </TableCell>
                                                <TableCell sx={{
                                                    fontWeight: 600,
                                                    fontSize: '14px',
                                                    color: theme.palette.text?.primary || 'rgba(255, 255, 255, 0.9)',
                                                    border: 'none'
                                                }}>
                                                    {t('division_2')}
                                                </TableCell>
                                                <TableCell sx={{
                                                    fontWeight: 600,
                                                    fontSize: '14px',
                                                    color: theme.palette.text?.primary || 'rgba(255, 255, 255, 0.9)',
                                                    border: 'none',
                                                    textAlign: 'center',
                                                    width: '70px'
                                                }}>
                                                    {t('main_organization')}
                                                </TableCell>
                                                <TableCell sx={{
                                                    fontWeight: 600,
                                                    fontSize: '14px',
                                                    color: theme.palette.text?.primary || 'rgba(255, 255, 255, 0.9)',
                                                    border: 'none',
                                                    textAlign: 'center',
                                                    width: '60px'
                                                }}>
                                                    {t('delete')}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {organizations.map((org) => (
                                                <TableRow key={org.id} sx={{
                                                    borderBottom: `1px solid ${theme.palette.figma.white[20]}`,
                                                    '&:last-child': {
                                                        borderBottom: 'none'
                                                    }
                                                }}>
                                                    <TableCell sx={{
                                                        border: 'none',
                                                        p: { xs: 0.5, sm: 1 }
                                                    }}>
                                                        <InputField
                                                            placeholder={t('please_enter_organization_name')}
                                                            value={org.organizationName}
                                                            onChange={(e) => handleOrganizationChange(org.id, 'organizationName', e.target.value)}
                                                            size="small"
                                                            disabled
                                                            showClearIcon={false}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        border: 'none',
                                                        p: { xs: 0.5, sm: 1 }                                                        //   minWidth: { xs: '100px', sm: '120px' }
                                                    }}>
                                                        {org.divisionSections && Object.keys(org.divisionSections).length > 0 ? (
                                                            <Dropdown
                                                                placeholder={t('division_1')}
                                                                value={org.divisionSection1}
                                                                onChange={(value) => handleOrganizationChange(org.id, 'divisionSection1', value)}
                                                                options={Object.keys(org.divisionSections || {}).map(key => ({
                                                                    value: key,
                                                                    label: key
                                                                }))}
                                                                size="small"
                                                            />
                                                        ) : (
                                                            <Typography sx={{
                                                                fontSize: '12px',
                                                                color: theme.palette.figma.white[60],
                                                                textAlign: 'center',
                                                                py: 1
                                                            }}>

                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        border: 'none',
                                                        p: { xs: 0.5, sm: 1 }
                                                    }}>
                                                        {org.divisionSection1 &&
                                                            org.divisionSections &&
                                                            org.divisionSections[org.divisionSection1] &&
                                                            org.divisionSections[org.divisionSection1].length > 0 ? (
                                                            <Dropdown
                                                                placeholder={t('division_2')}
                                                                value={org.divisionSection2}
                                                                onChange={(value) => handleOrganizationChange(org.id, 'divisionSection2', value)}
                                                                options={(org.divisionSections[org.divisionSection1] || []).map(item => ({
                                                                    value: item,
                                                                    label: item
                                                                }))}
                                                                size="small"
                                                            />
                                                        ) : (
                                                            <Typography sx={{
                                                                fontSize: '12px',
                                                                color: theme.palette.figma.white[60],
                                                                textAlign: 'center',
                                                                py: 1
                                                            }}>

                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        border: 'none',
                                                        p: { xs: 0.5, sm: 1 },
                                                        textAlign: 'center'
                                                        // width: '70px'
                                                    }}>
                                                        <Radio
                                                            checked={org.isMain}
                                                            onChange={() => handleMainOrganizationChange(org.id)}
                                                            sx={{
                                                                color: theme.palette.figma.white[60],
                                                                '&.Mui-checked': {
                                                                    color: theme.palette.figma.white[95],
                                                                },
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        border: 'none',
                                                        p: { xs: 0.5, sm: 1 },
                                                        textAlign: 'center'
                                                    }}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRemoveOrganization(org.id)}
                                                            sx={{
                                                                color: theme.palette.figma.white[60],
                                                                '&:hover': {
                                                                    color: theme.palette.error.main
                                                                }
                                                            }}
                                                        >
                                                            <Delete size={16} />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* 추가하기 버튼 - 어드민이 아닐 때만 표시 */}
                                {!isAdmin(meInfo?.grade) && (
                                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
                                        <Button
                                            size="small"
                                            onClick={handleAddOrganization}
                                            disabled={organizations.length >= 5}
                                            sx={{ minWidth: '100px' }}
                                        >
                                            {t('add')}
                                        </Button>
                                    </Box>
                                )}

                                {/* 안내 메시지 */}
                                <Box sx={{ mt: 2 }}>
                                    <Typography sx={{
                                        fontSize: '12px',
                                        color: theme.palette.figma.white[60],
                                        mb: 1
                                    }}>
                                        {t('max_5_affiliated_organizations')}
                                    </Typography>
                                    <Typography sx={{
                                        fontSize: '12px',
                                        color: theme.palette.figma.white[60]
                                    }}>
                                        {t('main_organization_one_only_points_shared')}
                                    </Typography>
                                </Box>
                            </Box>
                        </CommonCard>
                        {/* 하단 버튼 영역 */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'flex-end',
                            gap: 2,
                            px: 2.5,
                            pb: 3,
                            alignSelf: 'stretch',
                            marginTop: 'auto'
                        }}>
                            <Button
                                variant="gray"
                                onClick={handleCancel}
                                sx={{ minWidth: '120px' }}
                            >
                                {t('cancel')}
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={isLoading || !isFormValid()}
                                sx={{ minWidth: '120px' }}
                            >
                                {isLoading ? t('saving') : t('save')}
                            </Button>
                        </Box>

                    </Grid>
                </Grid>
            </Box>

            {/* 기관 검색 팝업 */}
            <Dialog
                open={isOrganizationSearchOpen}
                onClose={() => setIsOrganizationSearchOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        minHeight: '500px',
                    }
                }}
                sx={{
                    '& .MuiDialog-paper': {
                        padding: 2.5,
                        borderRadius: '12px',
                        backgroundColor: theme.palette.background.default,
                        border: `1px solid ${theme.palette.figma.white[20]}`
                    }
                }}
            >
                <OrganizationSearchContent
                    onSelectOrganization={handleSelectOrganization}
                    showHeader={true}
                    type="popup"
                    onClose={() => setIsOrganizationSearchOpen(false)}
                />
            </Dialog>

            {/* 팝업 */}
            <Popup
                open={popup.open}
                message={popup.message}
                subMessage={popup.subMessage}
                onConfirm={popup.onConfirm}
                onCancel={popup.onCancel}
            />
        </ContentArea>
    );
};

export { UserEdit };